/**
 * Ordered list of steps a jobseeker passes through on their first
 * questionnaire attempt
 */
export var FIRST_ATTEMPT_PAGES = [
    "antecedents",
    "knowledge",
    "generalSkills",
    "confidence",
    "attributes",
    "qualifications",
    "social",
    "personalWellbeing",
    "progression",
    "global",
    "clientObligations",
    "adviserObligations",
];
/**
 * On a second attempt, adviser feedback/impact and overall experience are
 * inserted after "global" and before the same obligations steps.
 */
export var SECOND_ATTEMPT_PAGES = [
    "antecedents",
    "knowledge",
    "generalSkills",
    "confidence",
    "attributes",
    "qualifications",
    "social",
    "personalWellbeing",
    "progression",
    "global",
    "adviserFeedback",
    "adviserImpact",
    "overallExperience",
    "clientObligations",
    "adviserObligations",
];
export var MILESTONE_ATTEMPT_PAGES = [
    "antecedents",
    "knowledge",
    "generalSkills",
    "confidence",
    "attributes",
    "qualifications",
    "social",
    "personalWellbeing",
    "progression",
    "global",
    "overallExperience",
    "clientObligations",
    "adviserObligations",
];
export function isMilestoneAttempt(quesAttemptDetails) {
    return !!((quesAttemptDetails === null || quesAttemptDetails === void 0 ? void 0 : quesAttemptDetails.milestone_questionnaire_ready) &&
        !(quesAttemptDetails === null || quesAttemptDetails === void 0 ? void 0 : quesAttemptDetails.milestone_completed));
}
export function getQuestionnaireAttemptVariant(quesAttemptDetails) {
    if (isMilestoneAttempt(quesAttemptDetails))
        return "milestone";
    return (quesAttemptDetails === null || quesAttemptDetails === void 0 ? void 0 : quesAttemptDetails.second_attempt_ready) ? "second" : "first";
}
/**
 * Computes the progress bar width for a given page, based on how many pages
 * precede it out of the total number of pages in the jobseeker's
 * questionnaire flow.
 */
export function getQuestionnaireProgressWidth(page, variant) {
    var resolvedVariant = typeof variant === "boolean" ? (variant ? "second" : "first") : variant;
    var pages = resolvedVariant === "milestone"
        ? MILESTONE_ATTEMPT_PAGES
        : resolvedVariant === "second"
            ? SECOND_ATTEMPT_PAGES
            : FIRST_ATTEMPT_PAGES;
    var index = pages.indexOf(page);
    var position = index === -1 ? pages.length : index;
    return "".concat(Math.round((position / pages.length) * 100), "%");
}
/**
 * Ordered list of steps an adviser passes through on their first
 * questionnaire attempt for a given jobseeker.
 */
export var ADVISER_FIRST_ATTEMPT_PAGES = [
    "adviserProgression",
    "adviserGlobal",
    "adviserClientObligations",
    "adviserAdviserObligations",
];
/**
 * On a second attempt, adviser jobseeker outcome is inserted after
 * "adviserGlobal" and before the same obligations steps.
 */
export var ADVISER_SECOND_ATTEMPT_PAGES = [
    "adviserProgression",
    "adviserGlobal",
    "adviserJobseekerOutcome",
    "adviserClientObligations",
    "adviserAdviserObligations",
];
/**
 * The progression milestone questionnaire (adviser-triggered, sits between
 * Questionnaire 1 and Questionnaire 2) mirrors the second-attempt shape but
 * swaps the outcome question for the progression milestone question.
 */
export var ADVISER_MILESTONE_ATTEMPT_PAGES = [
    "adviserProgression",
    "adviserGlobal",
    "adviserProgressionMilestone",
    "adviserClientObligations",
    "adviserAdviserObligations",
];
/**
 * Computes the progress bar width for a given page, based on how many pages
 * precede it out of the total number of pages in the adviser's
 * questionnaire flow.
 */
export function getAdviserQuestionnaireProgressWidth(page, variant) {
    // Boolean form kept for backwards compatibility with existing call sites:
    // true => "second", false => "first".
    var resolvedVariant = typeof variant === "boolean" ? (variant ? "second" : "first") : variant;
    var pages = resolvedVariant === "milestone"
        ? ADVISER_MILESTONE_ATTEMPT_PAGES
        : resolvedVariant === "second"
            ? ADVISER_SECOND_ATTEMPT_PAGES
            : ADVISER_FIRST_ATTEMPT_PAGES;
    var index = pages.indexOf(page);
    var position = index === -1 ? pages.length : index;
    return "".concat(Math.round((position / pages.length) * 100), "%");
}
/**
 * The jobseeker questionnaire pages that can be saved part-way through and
 * resumed in a later session, in the order they are presented. Each entry
 * ties the page's route to the API domain it submits to and to the key its
 * answers are held under in the JobSeekerQuestionnaireReducer.
 */
export var RESUMABLE_PAGES = [
    { path: "/questionnaire/antecedents", domain: "antecedents", formDataKey: "antecedentsFormData" },
    { path: "/questionnaire/knowledge", domain: "knowledge", formDataKey: "knowledeFormData" },
    { path: "/questionnaire/generalSkills", domain: "general_skills", formDataKey: "generalSkillsFormData" },
    { path: "/questionnaire/confidence", domain: "confidence", formDataKey: "confidenceFormData" },
    { path: "/questionnaire/attributes", domain: "attributes", formDataKey: "attributesFormData" },
    { path: "/questionnaire/qualifications", domain: "qualifications", formDataKey: "qualificationsFormData" },
    { path: "/questionnaire/social", domain: "social", formDataKey: "socialFormData" },
    { path: "/questionnaire/personal_wellbeing", domain: "personal_wellbeing", formDataKey: "personalWellbeingFormData" },
    { path: "/questionnaire/progression", domain: "progression", formDataKey: "progressionFormData" },
    { path: "/questionnaire/global", domain: "global", formDataKey: "globalFormData" },
    { path: "/questionnaire/adviserFeedback", domain: "adviser_feedback_2", formDataKey: "adviserFeedbackFormData" },
    { path: "/questionnaire/adviserImpact", domain: "adviser_impact_2", formDataKey: "adviserImpactFormData" },
    { path: "/questionnaire/overallExperience", domain: "overall_experience_2", formDataKey: "overallExperienceFormData" },
];
/** The API domain for a questionnaire route, if that page can be saved for later. */
export function getResumableDomain(pathname) {
    var _a;
    var normalised = pathname.replace(/\/+$/, "");
    return (_a = RESUMABLE_PAGES.find(function (page) { return page.path === normalised; })) === null || _a === void 0 ? void 0 : _a.domain;
}
/** Saved-page domains that are not part of the progression milestone attempt. */
var MILESTONE_SKIPPED_DOMAINS = ["adviser_feedback_2", "adviser_impact_2"];
/**
 * The route a jobseeker should continue from: the first page they have not
 * yet submitted. With nothing saved this is the start of the questionnaire.
 */
export function getResumePath(pages, milestoneAttempt) {
    if (milestoneAttempt === void 0) { milestoneAttempt = false; }
    var next = RESUMABLE_PAGES.find(function (page) {
        var _a;
        return !(milestoneAttempt && MILESTONE_SKIPPED_DOMAINS.includes(page.domain)) &&
            !((_a = pages === null || pages === void 0 ? void 0 : pages[page.domain]) === null || _a === void 0 ? void 0 : _a.submitted);
    });
    return (next !== null && next !== void 0 ? next : RESUMABLE_PAGES[0]).path;
}
/**
 * Reduces a page's in-progress answers to what is worth saving: unanswered
 * questions are dropped and multiple-choice selections are stored
 * comma-separated, which is the form the questionnaire pages restore from.
 */
export function toDraftAnswers(formData) {
    var draft = {};
    Object.entries(formData).forEach(function (_a) {
        var key = _a[0], value = _a[1];
        var answer = Array.isArray(value) ? value.filter(Boolean).join(",") : value;
        if (answer) {
            draft[key] = answer;
        }
    });
    return draft;
}
